import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

import {
  AnalyticsService,
  DmmViolationNotice,
} from '../../analytics/analytics.service';
import { DmmViolationDialogComponent } from '../dmm-violation-dialog/dmm-violation-dialog.component';

export type DmmViolationBannerMode = 'global' | 'contextual';

/**
 * Banner fuer DMM-Violation-Hinweise.
 *
 * - mode='global' (Default): laed Notices per OnInit selbst und zeigt
 *   abhaengig von Anzahl/Coach-Modus Athletennamen + Severity-Hinweise.
 * - mode='contextual': erwartet bereits gefilterte Notices via [notices]-
 *   Input. Z.B. fuer Session-Detail oder Athleten-Liste.
 */
@Component({
  selector: 'app-dmm-violation-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
  ],
  template: `
    @if (visibleNotices.length > 0) {
      <div class="dv-banner" [class.dv-banner--coach]="visibleNotices.length > 1">
        <mat-icon class="dv-icon">info</mat-icon>

        <div class="dv-text">
          @if (visibleNotices.length === 1) {
            <strong>
              @if (visibleNotices[0].athlete_name && !isSelfNotice(visibleNotices[0])) {
                {{ keyFor(visibleNotices[0], 'title_athlete') | translate }} —
                {{ visibleNotices[0].athlete_name }}
              } @else {
                {{ keyFor(visibleNotices[0], 'title_athlete') | translate }}
              }
            </strong>
            <span class="dv-body">
              {{ keyFor(visibleNotices[0], 'body_athlete') | translate:{
                   n: visibleNotices[0].sessions_total,
                   minutes: overCapacityMinutes(visibleNotices[0]),
                   since: (visibleNotices[0].window_start | date:'dd.MM.yyyy') } }}
            </span>
          } @else {
            <strong>
              {{ 'diagnostics.dmm_violation.banner.title_coach' | translate:{ n: visibleNotices.length } }}
            </strong>
            <ul class="dv-list">
              @for (n of visibleNotices; track n.id) {
                <li>
                  <a (click)="openDialog(n)">
                    {{ n.athlete_name || ('User #' + n.user_id) }}
                    <small>({{ n.sessions_total }})</small>
                  </a>
                </li>
              }
            </ul>
          }
        </div>

        <span class="grow"></span>
        @if (visibleNotices.length === 1) {
          <button mat-button color="primary" (click)="openDialog(visibleNotices[0])">
            {{ 'diagnostics.dmm_violation.banner.cta' | translate }}
          </button>
        }
        <button mat-icon-button (click)="hideBanner()" aria-label="dismiss">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    }
  `,
  styles: [`
    .dv-banner {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      padding: 0.6rem 0.85rem;
      margin: 0.5rem 0;
      background: #ede7f6;
      color: #4527a0;
      border-left: 4px solid #5e35b1;
      border-radius: 6px;
      font-size: var(--fs-sm);
    }
    .dv-banner--coach { background: #e8eaf6; color: #1a237e; border-left-color: #283593; }
    .dv-icon { color: #5e35b1; flex-shrink: 0; margin-top: 0.1rem; }
    .dv-banner--coach .dv-icon { color: #283593; }
    .dv-text { flex: 1; min-width: 0; }
    .dv-text strong { display: block; }
    .dv-body { display: block; margin-top: 0.15rem; color: inherit; }
    .dv-list { margin: 0.35rem 0 0; padding-left: 1.25rem; }
    .dv-list a { cursor: pointer; text-decoration: underline; color: inherit; }
    .dv-list small { color: var(--text-secondary, #888); margin-left: 0.25rem; }
    .grow { flex: 1; }
  `],
})
export class DmmViolationBannerComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() mode: DmmViolationBannerMode = 'global';
  @Input() notices: DmmViolationNotice[] | null = null;
  /** User-ID des aktuell eingeloggten Users — fuer "isSelfNotice"-Heuristik. */
  @Input() currentUserId: number | null = null;

  visibleNotices: DmmViolationNotice[] = [];
  private suppressed = false;

  ngOnInit(): void {
    if (this.mode === 'contextual') {
      this.visibleNotices = this.notices ?? [];
      return;
    }
    this.refresh();
  }

  private refresh(): void {
    this.analytics.getDmmViolationNotices('open').subscribe({
      next: (resp) => {
        this.visibleNotices = this.suppressed ? [] : (resp.notices ?? []);
        this.cdr.markForCheck();
      },
      error: () => {
        this.visibleNotices = [];
        this.cdr.markForCheck();
      },
    });
  }

  hideBanner(): void {
    this.suppressed = true;
    this.visibleNotices = [];
    this.cdr.markForCheck();
  }

  openDialog(notice: DmmViolationNotice): void {
    const ref = this.dialog.open(DmmViolationDialogComponent, {
      data: { notice },
      width: '720px',
      maxWidth: '95vw',
    });
    ref.afterClosed().subscribe((result) => {
      // Im Per-Session-Modell kann der Dialog auch dann mit aktualisiertem
      // Stand schliessen wenn der User nur einzelne Zeilen ack'd hat (ohne
      // dass die Notice resolved wurde). In dem Fall vom Server frisch
      // laden, damit der Counter im Banner stimmt.
      if (result?.resolved) {
        this.visibleNotices = this.visibleNotices.filter((n) => n.id !== notice.id);
        this.cdr.markForCheck();
      } else if (result?.acknowledged) {
        this.refresh();
      }
    });
  }

  isSelfNotice(n: DmmViolationNotice): boolean {
    return this.currentUserId != null && n.user_id === this.currentUserId;
  }

  /** Kapazitaets-Hinweis ("Parameter vermutlich veraltet") hat eigene Texte;
   *  ohne `kind` (Backend vor der Einfrierregel) gilt der alte Abbruch-Hinweis. */
  keyFor(n: DmmViolationNotice, field: string): string {
    const group = n.kind === 'over_capacity' ? 'over_capacity_banner' : 'banner';
    return `diagnostics.dmm_violation.${group}.${field}`;
  }

  overCapacityMinutes(n: DmmViolationNotice): number {
    return Math.round((n.over_capacity_s_total ?? 0) / 60);
  }
}
